import * as React from "react";
import { useEffect, useState } from "react";
import { Button, Dropdown, Grid, Header, Modal, Message } from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-monokai";
import { AIAgentTask } from "../../../../../generated/api";

export interface ConversionResultsComparisonModalProps {
    isOpen: boolean;
    onClose: () => void;
    aiAgentTask: AIAgentTask | null;
}

interface IterationOption {
    key: string;
    text: string;
    value: number;
}

export function ConversionResultsComparisonModal(props: ConversionResultsComparisonModalProps) {
    const [iterationOptions, setIterationOptions] = useState<IterationOption[]>([]);
    const [leftSelection, setLeftSelection] = useState<number>(-1);
    const [rightSelection, setRightSelection] = useState<number>(-1);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (!props.aiAgentTask?.iterations) return;

        const options: IterationOption[] = [];

        props.aiAgentTask.iterations.forEach((iteration, iterIndex) => {
            if (iteration.queuedEventConversionResponse?.resultFiles?.length > 0) {
                const source = iteration.functionCallOutput?.executeFunc || "Unknown";
                options.push({
                    key: `iter-${iterIndex}`,
                    text: `Iteration ${iterIndex + 1}: ${source}`,
                    value: iterIndex
                });
            }
        });

        setIterationOptions(options);

        if (!isInitialized && options.length > 0) {
            if (options.length >= 2) {
                setLeftSelection(options[0].value);
                setRightSelection(options[options.length - 1].value);
            } else if (options.length === 1) {
                setLeftSelection(options[0].value);
                setRightSelection(options[0].value);
            }
            setIsInitialized(true);
        }
    }, [props.aiAgentTask, isInitialized]);

    useEffect(() => {
        if (props.isOpen) {
            setIsInitialized(false);
        }
    }, [props.isOpen]);

    const getIterationContent = (iterationIndex: number): string => {
        if (iterationIndex === -1 || !props.aiAgentTask?.iterations) return "";

        const iteration = props.aiAgentTask.iterations[iterationIndex];
        const firstFile = iteration?.queuedEventConversionResponse?.resultFiles?.[0];

        if (!firstFile?.base64Content) return "";

        try {
            return atob(firstFile.base64Content);
        } catch (error) {
            return "Error decoding file content";
        }
    };

    const getIterationFileName = (iterationIndex: number): string => {
        if (iterationIndex === -1 || !props.aiAgentTask?.iterations) return "";

        const iteration = props.aiAgentTask.iterations[iterationIndex];
        const firstFile = iteration?.queuedEventConversionResponse?.resultFiles?.[0];

        return firstFile?.fileName || `File from Iteration ${iterationIndex + 1}`;
    };

    // Check if there are no conversion files
    if (iterationOptions.length === 0) {
        return (
            <Modal open={props.isOpen} onClose={props.onClose} size="small">
                <Header content="No Conversion Files" />
                <Modal.Content>
                    <Message warning>No conversion result files available for comparison.</Message>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={props.onClose}>Close</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    // Check if there's only one iteration with conversion files
    if (iterationOptions.length === 1) {
        return (
            <Modal open={props.isOpen} onClose={props.onClose} size="small">
                <Header content="Cannot Compare Results" />
                <Modal.Content>
                    <Message info>
                        Only one conversion result is available. You need at least two conversion results to perform a comparison.
                    </Message>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={props.onClose}>Close</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    const leftContent = getIterationContent(leftSelection);
    const rightContent = getIterationContent(rightSelection);
    const leftFileName = getIterationFileName(leftSelection);
    const rightFileName = getIterationFileName(rightSelection);

    return (
        <Modal open={props.isOpen} onClose={props.onClose} size="fullscreen">
            <Header content="Compare Conversion Results" />
            <Modal.Content style={{ height: "85vh", padding: "15px" }}>
                <Grid style={{ marginBottom: "15px", height: "100%" }}>
                    <Grid.Row style={{ paddingBottom: "10px" }}>
                        <Grid.Column width={8}>
                            <Dropdown
                                fluid
                                selection
                                options={iterationOptions}
                                value={leftSelection}
                                onChange={(_, data) => setLeftSelection(data.value as number)}
                                placeholder="Select iteration"
                            />
                        </Grid.Column>
                        <Grid.Column width={8}>
                            <Dropdown
                                fluid
                                selection
                                options={iterationOptions}
                                value={rightSelection}
                                onChange={(_, data) => setRightSelection(data.value as number)}
                                placeholder="Select iteration"
                            />
                        </Grid.Column>
                    </Grid.Row>

                    {leftContent && rightContent ? (
                        <Grid.Row style={{ height: "calc(100% - 80px)", paddingTop: "0" }}>
                            <Grid.Column width={8} style={{ height: "100%", paddingRight: "8px" }}>
                                <div style={{
                                    fontSize: "12px",
                                    marginBottom: "8px",
                                    padding: "5px 8px",
                                    backgroundColor: "#f0f0f0",
                                    borderRadius: "3px",
                                    color: "#333"
                                }}>
                                    {leftFileName}
                                </div>
                                <AceEditor
                                    mode='groovy'
                                    theme="monokai"
                                    value={leftContent}
                                    readOnly
                                    width="100%"
                                    height="calc(100% - 5px)"
                                    fontSize={13}
                                    showPrintMargin={false}
                                    wrapEnabled={true}
                                    setOptions={{
                                        showLineNumbers: true,
                                        tabSize: 2,
                                        useWorker: false
                                    }}
                                />
                            </Grid.Column>
                            <Grid.Column width={8} style={{ height: "100%", paddingLeft: "8px" }}>
                                <div style={{
                                    fontSize: "12px",
                                    marginBottom: "8px",
                                    padding: "5px 8px",
                                    backgroundColor: "#f0f0f0",
                                    borderRadius: "3px",
                                    color: "#333"
                                }}>
                                    {rightFileName}
                                </div>
                                <AceEditor
                                    mode='groovy'
                                    theme="monokai"
                                    value={rightContent}
                                    readOnly
                                    width="100%"
                                    height="calc(100% - 5px)"
                                    fontSize={13}
                                    showPrintMargin={false}
                                    wrapEnabled={true}
                                    setOptions={{
                                        showLineNumbers: true,
                                        tabSize: 2,
                                        useWorker: false
                                    }}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    ) : (
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Message info>Please select iterations to compare</Message>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={props.onClose} color="blue" size="large">Close</Button>
            </Modal.Actions>
        </Modal>
    );
}